import { useCallback, useEffect, useState } from "react";
import { Plus, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../../lib/api";
import { Badge, PRIO, card, inputCls, btnPrimary, btnGhost, fmtDateTime } from "./common";

const TASK_STATUS = { OPEN: ["Offen", "bg-blue-100 text-blue-700"], IN_PROGRESS: ["In Arbeit", "bg-amber-100 text-amber-700"], DONE: ["Erledigt", "bg-green-100 text-green-700"] };

export default function PanelTasks({ me }) {
  const [tasks, setTasks] = useState(null);
  const [scope, setScope] = useState("mine");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", assignee_id: "", due_at: "", priority: "NORMAL" });
  const [employees, setEmployees] = useState([]);
  const [commentFor, setCommentFor] = useState(null);
  const [comment, setComment] = useState("");

  useEffect(() => {
    api.get("/admin/panel/overview").then(({ data }) => setEmployees((data.per_employee || []).filter((e) => e.status === "ACTIVE"))).catch(() => {});
  }, []);
  const load = useCallback(() => api.get(`/admin/tasks?scope=${scope}`).then(({ data }) => setTasks(data.tasks)).catch((e) => toast.error(errText(e))), [scope]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      await api.post("/admin/tasks", { ...form, assignee_id: form.assignee_id || null, due_at: form.due_at ? new Date(form.due_at).toISOString() : null });
      toast.success("Aufgabe erstellt");
      setShowForm(false);
      setForm({ title: "", description: "", assignee_id: "", due_at: "", priority: "NORMAL" });
      load();
    } catch (e) { toast.error(errText(e)); }
  };
  const update = async (id, patch, ok) => {
    try { await api.patch(`/admin/tasks/${id}`, patch); ok && toast.success(ok); load(); } catch (e) { toast.error(errText(e)); }
  };
  const addComment = async (id) => {
    try { await api.post(`/admin/tasks/${id}/comment`, { body: comment }); setComment(""); setCommentFor(null); load(); } catch (e) { toast.error(errText(e)); }
  };
  const overdue = (t) => t.due_at && t.status !== "DONE" && new Date(t.due_at) < new Date();

  return (
    <div className="space-y-4" data-testid="panel-tasks">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex bg-secondary/80 rounded-full p-1">
          {[["mine", "Meine Aufgaben"], ["all", "Alle"]].map(([k, l]) => (
            <button key={k} onClick={() => setScope(k)} className={`text-xs font-bold px-4 py-2 rounded-full ${scope === k ? "bg-[#111111] text-white" : "text-foreground/60"}`} data-testid={`tasks-scope-${k}`}>{l}</button>
          ))}
        </div>
        <button className={`${btnPrimary} ml-auto inline-flex items-center gap-1.5`} onClick={() => setShowForm(!showForm)} data-testid="task-new-btn"><Plus className="w-4 h-4" /> Neue Aufgabe</button>
      </div>
      {showForm && (
        <div className={`${card} p-5 space-y-3`} data-testid="task-form">
          <input className={inputCls} placeholder="Titel" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title-input" />
          <textarea className={`${inputCls} min-h-[70px]`} placeholder="Beschreibung (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <div className="grid sm:grid-cols-3 gap-2">
            <select className="text-sm border border-border rounded-xl px-3 py-2.5 bg-white" value={form.assignee_id} onChange={(e) => setForm({ ...form, assignee_id: e.target.value })} data-testid="task-assignee-select">
              <option value="">Nicht zugewiesen</option>
              {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.name || emp.username}</option>)}
            </select>
            <input type="datetime-local" className={inputCls} value={form.due_at} onChange={(e) => setForm({ ...form, due_at: e.target.value })} data-testid="task-due-input" />
            <select className="text-sm border border-border rounded-xl px-3 py-2.5 bg-white" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
              {Object.entries(PRIO).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <button className={btnPrimary} disabled={form.title.length < 2} onClick={create} data-testid="task-create-btn">Aufgabe anlegen</button>
        </div>
      )}
      <div className="space-y-2.5" data-testid="tasks-list">
        {tasks === null && <div className="h-32 bg-muted rounded-2xl animate-pulse" />}
        {tasks?.length === 0 && <p className={`${card} p-6 text-center text-sm text-muted-foreground`}>Keine Aufgaben vorhanden.</p>}
        {tasks?.map((t) => (
          <div key={t.id} className={`${card} p-4 ${overdue(t) ? "border-red-300" : ""}`} data-testid={`task-${t.id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <Badge map={TASK_STATUS} value={t.status} />
              <Badge map={PRIO} value={t.priority} />
              {overdue(t) && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700">Überfällig</span>}
              <span className="text-xs text-muted-foreground ml-auto">{t.due_at ? `Fällig: ${fmtDateTime(t.due_at)}` : "Keine Frist"}</span>
            </div>
            <p className="font-bold text-sm mt-2">{t.title}</p>
            {t.description && <p className="text-sm text-muted-foreground mt-0.5 whitespace-pre-wrap">{t.description}</p>}
            <p className="text-xs text-muted-foreground mt-1.5">Zugewiesen: <b>{t.assignee_name || "—"}</b> · Erstellt von {t.creator_name}</p>
            {(t.comments || []).map((c) => (
              <p key={c.id} className="text-xs bg-secondary/60 rounded-lg px-3 py-2 mt-2"><b>{c.author_name}:</b> {c.body}</p>
            ))}
            <div className="flex flex-wrap gap-2 mt-3">
              {t.status !== "DONE" && <button className={btnGhost} onClick={() => update(t.id, { status: t.status === "OPEN" ? "IN_PROGRESS" : "DONE" }, "Status aktualisiert")} data-testid={`task-advance-${t.id}`}>{t.status === "OPEN" ? "In Arbeit nehmen" : "Als erledigt markieren"}</button>}
              {t.status === "DONE" && <button className={btnGhost} onClick={() => update(t.id, { status: "OPEN" })}>Wieder öffnen</button>}
              {!t.assignee_id && <button className={btnGhost} onClick={() => update(t.id, { assignee_id: me.id }, "Übernommen")}>Übernehmen</button>}
              <button className={`${btnGhost} inline-flex items-center gap-1`} onClick={() => setCommentFor(commentFor === t.id ? null : t.id)}><MessageSquare className="w-3.5 h-3.5" /> Kommentar</button>
            </div>
            {commentFor === t.id && (
              <div className="flex gap-2 mt-2">
                <input className={inputCls} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Interner Kommentar…" data-testid="task-comment-input" />
                <button className={btnGhost} disabled={!comment.trim()} onClick={() => addComment(t.id)} data-testid="task-comment-btn">Senden</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
